import { useState, useRef, useEffect } from 'react';
import { Button } from 'base/src/components/Button';
import { Icon } from '@iconify/react';
import * as S from './Modal2FAStyles';
import PagarPedidoContainer from '../../../../containers/pagarPedido/pagarPedidoContainer';
import { DuploFatorRequest } from '../../../../types/pagarPedido/duploFator';
import { telefoneMask } from 'base/src/utils/stringUtil';

export interface Modal2FAProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (code: string) => void;
  userPhone?: string;
  isLoading?: boolean;
  usuarioID?: number;
  sistemaID?: number;
}

function Modal2FA(props: Modal2FAProps) {
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [sendError, setSendError] = useState<string>('');

  const _pagarPedidoContainer = new PagarPedidoContainer();

  // Countdown para reenvio
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Reset ao fechar modal
  useEffect(() => {
    if (!props.isOpen) {
      setCode(['', '', '', '', '', '']);
      setIsCodeSent(false);
      setCountdown(0);
      setIsSendingCode(false);
      setSendError('');
    }
  }, [props.isOpen]);

  async function enviarCodigoReal() {
    if (!props.usuarioID || !props.sistemaID || !props.userPhone) {
      setSendError('Dados necessários não fornecidos para envio do código');
      return;
    }

    setSendError('');
    setIsSendingCode(true);

    try {
      const request: DuploFatorRequest = {
        UsuarioID: props.usuarioID,
        SistemaID: props.sistemaID,
        CodigoTipoID: 1,
        TelefoneDestino: props.userPhone,
        Acao: 'PAGAMENTO' // Ação específica para pagamento
      };

      const result = await _pagarPedidoContainer.enviarCodigoWhatsApp(request);

      if (result instanceof Error) {
        setSendError('Não foi possível enviar o código. Tente novamente.');
        setIsSendingCode(false);
        return;
      }

      // Código enviado com sucesso
      setIsCodeSent(true);
      setCountdown(60); // 60 segundos para reenvio
      setIsSendingCode(false);
    } catch (error) {
      setSendError('Não foi possível enviar o código. Tente novamente.');
      setIsSendingCode(false);
    }
  }

  function handleInputChange(index: number, value: string) {
    // Apenas números
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Apenas o último dígito
    setCode(newCode);

    // Move para o próximo input automaticamente
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move para o input anterior se estiver vazio
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];

    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }

    setCode(newCode);

    // Focus no próximo input vazio ou no último
    const nextEmptyIndex = newCode.findIndex(digit => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : Math.min(nextEmptyIndex, 5);
    inputRefs.current[focusIndex]?.focus();
  }

  const isCodeComplete = code.every(digit => digit !== '');
  const fullCode = code.join('');

  function handleConfirm() {
    if (isCodeComplete) {
      props.onConfirm(fullCode);
    }
  }

  function handleResendCode() {
    setCode(['', '', '', '', '', '']);
    setIsCodeSent(false);
    setIsSendingCode(false);
    setSendError('');
    // Usuário terá que clicar no botão "Enviar Código" novamente
  }

  function setInputRef(index: number) {
    return (el: HTMLInputElement | null) => {
      inputRefs.current[index] = el;
    };
  }

  function handleInputChangeWrapper(index: number) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      handleInputChange(index, e.target.value);
    };
  }

  function handleKeyDownWrapper(index: number) {
    return (e: React.KeyboardEvent<HTMLInputElement>) => {
      handleKeyDown(index, e);
    };
  }

  if (!props.isOpen) return null;

  return (
    <S.Overlay>
      <S.ModalContainer>
        <S.Header>
          <S.Title>Autenticação de Segurança</S.Title>
          <S.CloseButton onClick={props.onClose}>
            <Icon icon="ph:x" width={20} />
          </S.CloseButton>
        </S.Header>

        <S.Content>
          <S.IconContainer>
            <Icon icon="ph:shield-check" width={48} color="#28a745" />
          </S.IconContainer>

          {!isCodeSent && !isSendingCode ? (
            <>
              <S.Description>
                Para confirmar o pagamento, enviaremos um código de verificação para:
              </S.Description>
              <S.PhoneNumber>
                {telefoneMask(props.userPhone || '(11) 99999-9999')}
              </S.PhoneNumber>

              {sendError && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fee',
                  borderRadius: '6px',
                  color: '#c53030',
                  fontSize: '14px',
                  textAlign: 'center',
                  marginTop: '16px',
                  border: '1px solid #feb2b2'
                }}>
                  {sendError}
                </div>
              )}

              <div style={{ marginTop: '20px' }}>
                <Button
                  variant="primary"
                  size="md"
                  onClick={enviarCodigoReal}
                  disabled={isSendingCode}
                >
                  <Icon icon="ph:paper-plane-tilt" width={16} />
                  Enviar Código
                </Button>
              </div>
            </>
          ) : isSendingCode ? (
            <S.LoadingState>
              <Icon icon="ph:spinner" width={24} className="spinning" />
              <S.Description>Enviando código de verificação...</S.Description>
            </S.LoadingState>
          ) : (
            <>
              <S.Description>
                Digite o código de 6 dígitos enviado para o número:
              </S.Description>
              <S.PhoneNumber>
                {telefoneMask(props.userPhone || '(11) 99999-9999')}
              </S.PhoneNumber>

              <S.CodeInputContainer>
                {code.map((digit, index) => (
                  <S.CodeInput
                    key={index}
                    ref={setInputRef(index)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={handleInputChangeWrapper(index)}
                    onKeyDown={handleKeyDownWrapper(index)}
                    onPaste={handlePaste}
                    autoFocus={index === 0}
                  />
                ))}
              </S.CodeInputContainer>

              <S.ResendContainer>
                {countdown > 0 ? (
                  <S.CountdownText>
                    Tentar novamente em {countdown}s
                  </S.CountdownText>
                ) : (
                  <S.ResendButton onClick={handleResendCode}>
                    <Icon icon="ph:arrow-clockwise" width={16} />
                    Não recebeu? Tentar novamente
                  </S.ResendButton>
                )}
              </S.ResendContainer>
            </>
          )}
        </S.Content>

        <S.Footer>
          <Button
            variant="secondary"
            size="md"
            full
            onClick={props.onClose}
            disabled={props.isLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="md"
            full
            disabled={!isCodeComplete || props.isLoading || !isCodeSent}
            onClick={handleConfirm}
          >
            {props.isLoading ? (
              <>
                <Icon icon="ph:spinner" width={16} className="spinning" />
                Verificando...
              </>
            ) : (
              'Confirmar Pagamento'
            )}
          </Button>
        </S.Footer>
      </S.ModalContainer>
    </S.Overlay>
  );
}

export default Modal2FA;
